import { Directive, ElementRef, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * CPF/CNPJ mask directive that provides format-as-you-type behavior.
 * Automatically switches between CPF and CNPJ format based on input length:
 * - CPF (11 digits or less): 000.000.000-00
 * - CNPJ (12+ digits): 00.000.000/0000-00
 *
 * Usage:
 * ```html
 * <input appCpfCnpjMask formControlName="cpfCnpj" />
 * ```
 *
 * Note: The form control receives the FORMATTED value (with dots, dash, slash).
 * The validator should handle stripping non-digits if needed.
 */
@Directive({
  selector: '[appCpfCnpjMask]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: CpfCnpjMaskDirective,
      multi: true,
    },
  ],
  host: {
    '(input)': 'onInput($event)',
    '(blur)': 'onTouched()',
    '(keydown)': 'onKeydown($event)',
    '[attr.inputmode]': '"numeric"',
  },
})
export class CpfCnpjMaskDirective implements ControlValueAccessor {
  private readonly _el = inject(ElementRef<HTMLInputElement>);
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private _onChange: (value: string) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private _onTouched: () => void = () => {};

  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const cursorPosition = input.selectionStart || 0;
    const oldValue = input.value;

    // Extract only digits, max 14 (CNPJ length)
    const rawDigits = oldValue.replace(/\D/g, '').slice(0, 14);

    // Format based on length
    const formatted = this.formatCpfCnpj(rawDigits);

    // Only update if the formatted value is different from current
    if (input.value !== formatted) {
      // Calculate new cursor position before updating value
      const newCursorPosition = this.calculateCursorPosition(oldValue, formatted, cursorPosition);

      // Update input value with formatted version
      input.value = formatted;

      // Restore cursor position
      requestAnimationFrame(() => {
        input.setSelectionRange(newCursorPosition, newCursorPosition);
      });
    }

    // Notify Angular forms of the change (formatted value)
    this._onChange(formatted);
  }

  onKeydown(event: KeyboardEvent): void {
    // Allow: backspace, delete, tab, escape, enter
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter'];
    // Allow: Ctrl/Cmd+A, Ctrl/Cmd+C, Ctrl/Cmd+V, Ctrl/Cmd+X
    const isCtrlCmd = event.ctrlKey || event.metaKey;
    const allowedWithCtrl = ['a', 'c', 'v', 'x'];
    // Allow: home, end, left, right arrows
    const navigationKeys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];

    if (
      allowedKeys.includes(event.key) ||
      navigationKeys.includes(event.key) ||
      (isCtrlCmd && allowedWithCtrl.includes(event.key.toLowerCase()))
    ) {
      return;
    }

    // Allow numbers only
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    if (value) {
      // Extract digits and format for display
      const rawDigits = value.replace(/\D/g, '').slice(0, 14);
      this._el.nativeElement.value = this.formatCpfCnpj(rawDigits);
    } else {
      this._el.nativeElement.value = '';
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._el.nativeElement.disabled = isDisabled;
  }

  onTouched(): void {
    this._onTouched();
  }

  private formatCpfCnpj(digits: string): string {
    if (digits.length === 0) {
      return '';
    }

    // If 12 or more digits, format as CNPJ: 00.000.000/0000-00
    if (digits.length > 11) {
      return this.formatCnpj(digits);
    }

    // Otherwise, format as CPF: 000.000.000-00
    return this.formatCpf(digits);
  }

  private formatCpf(digits: string): string {
    // CPF format: 000.000.000-00
    let result = '';

    for (let i = 0; i < digits.length && i < 11; i++) {
      if (i === 3 || i === 6) {
        result += '.';
      } else if (i === 9) {
        result += '-';
      }
      result += digits[i];
    }

    return result;
  }

  private formatCnpj(digits: string): string {
    // CNPJ format: 00.000.000/0000-00
    let result = '';

    for (let i = 0; i < digits.length && i < 14; i++) {
      if (i === 2 || i === 5) {
        result += '.';
      } else if (i === 8) {
        result += '/';
      } else if (i === 12) {
        result += '-';
      }
      result += digits[i];
    }

    return result;
  }

  private calculateCursorPosition(oldValue: string, newValue: string, oldPosition: number): number {
    // Count digits before cursor in old value
    const digitsBeforeCursor = oldValue.slice(0, oldPosition).replace(/\D/g, '').length;

    // Find position in new value that has the same number of digits before it
    let digitCount = 0;
    for (let i = 0; i < newValue.length; i++) {
      if (/\d/.test(newValue[i])) {
        digitCount++;
      }
      if (digitCount === digitsBeforeCursor) {
        // Position after the last matched digit
        return i + 1;
      }
    }

    return newValue.length;
  }
}
